import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { API } from "@/lib/api";

/**
 * Subscribes to a ring room. Uses WebSocket for low-latency updates with a
 * REST polling fallback. Returns { snapshot, connected, send }.
 */
export function useRoom(ringId, token) {
  const [snapshot, setSnapshot] = useState(null);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const pollRef = useRef(null);
  const hbRef = useRef(null);
  const closedRef = useRef(false);

  useEffect(() => {
    if (!ringId || !token) return;
    closedRef.current = false;
    const wsBase = API.startsWith("http")
      ? API.replace(/^http/, "ws")
      : `${window.location.origin.replace(/^http/, "ws")}${API}`;

    const poll = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [c, j] = await Promise.all([
          axios.get(`${API}/rings/${ringId}/current`, { headers }),
          axios.get(`${API}/rings/${ringId}/judges`, { headers }),
        ]);
        setSnapshot({
          type: "state", match: c.data.match, judges: j.data.judges,
          ready_count: j.data.ready_count, num_judges: c.data.match?.config?.num_judges || 0,
        });
      } catch (e) { console.debug("room poll skipped:", e?.message); }
    };
    const startPoll = () => { clearInterval(pollRef.current); poll(); pollRef.current = setInterval(poll, 1000); };

    const connect = () => {
      try {
        const ws = new WebSocket(`${wsBase}/ws/ring/${ringId}?token=${encodeURIComponent(token)}`);
        wsRef.current = ws;
        ws.onopen = () => {
          setConnected(true);
          // keep a low-frequency safety poll so any missed WS update self-heals
          clearInterval(pollRef.current);
          pollRef.current = setInterval(poll, 3000);
          clearInterval(hbRef.current);
          hbRef.current = setInterval(() => { try { ws.send(JSON.stringify({ type: "ping" })); } catch (e) { console.debug("ws ping skipped (socket closing)", e?.message); } }, 10000);
        };
        ws.onmessage = (e) => { try { const d = JSON.parse(e.data); if (d.type === "state") setSnapshot(d); } catch (err) { console.debug("ws message parse failed", err); } };
        ws.onclose = () => { setConnected(false); clearInterval(hbRef.current); if (!closedRef.current) { startPoll(); setTimeout(connect, 2000); } };
        ws.onerror = () => { try { ws.close(); } catch (e) { console.debug("ws close on error failed", e?.message); } };
      } catch { startPoll(); }
    };

    connect();
    startPoll();
    return () => { closedRef.current = true; clearInterval(pollRef.current); clearInterval(hbRef.current); try { wsRef.current?.close(); } catch (e) { console.debug("ws cleanup close failed", e?.message); } };
  }, [ringId, token]);

  const send = useCallback((obj) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === 1) { ws.send(JSON.stringify(obj)); return true; }
    return false;
  }, []);

  return { snapshot, connected, send };
}
